// useConversationStream — subscribe to the live conversations SSE feed.
//
// The gateway authenticates the stream via the httpOnly session cookie, so the
// EventSource is opened with { withCredentials: true } and NO token in the URL
// (same as the notifications bell). The feed carries the caller's new messages
// across ALL their conversations; the caller filters by conversation_id.
//
// Low-bandwidth / resilience: every message frame carries an `id:` (a resume
// cursor), so the browser sends Last-Event-ID automatically on reconnect and the
// server replays the exact gap from Postgres — no message lost on a dropped
// connection. We still close + reconnect with backoff on error/expiry (a fresh
// cookie), which also stops the browser's native tight reconnect loop.
import { useEffect, useRef } from 'react';
import { apiConfig } from '@/config/api.config';
import type { Message } from '../types';

type OnMessage = (message: Message) => void;

interface Options {
  /** Called for every live message.created frame (all the caller's conversations). */
  onMessage: OnMessage;
  /** Pause the stream (e.g. when the feature is not entitled / not mounted). */
  enabled?: boolean;
  onStatusChange?: (status: 'connecting' | 'open' | 'closed') => void;
}

const RECONNECT_MIN = 1000;
const RECONNECT_MAX = 30000;

export function useConversationStream({ onMessage, enabled = true, onStatusChange }: Options): void {
  const onMessageRef = useRef<OnMessage>(onMessage);
  const onStatusRef = useRef(onStatusChange);
  onMessageRef.current = onMessage;
  onStatusRef.current = onStatusChange;

  useEffect(() => {
    if (!enabled) return;

    let es: EventSource | null = null;
    let closed = false;
    let backoff = RECONNECT_MIN;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const status = (s: 'connecting' | 'open' | 'closed') => onStatusRef.current?.(s);
    const streamUrl = `${apiConfig.baseURL}/api/v1/conversations/stream`;

    const scheduleReconnect = () => {
      if (closed || reconnectTimer) return;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, backoff);
      backoff = Math.min(backoff * 2, RECONNECT_MAX);
    };

    const connect = () => {
      if (closed) return;
      status('connecting');
      es = new EventSource(streamUrl, { withCredentials: true });

      es.addEventListener('ready', () => {
        backoff = RECONNECT_MIN; // healthy connection resets the backoff
        status('open');
      });

      es.addEventListener('message.created', (ev: MessageEvent) => {
        try {
          onMessageRef.current(JSON.parse(ev.data) as Message);
        } catch {
          /* ignore a malformed frame */
        }
      });

      // Server caps the stream at the token lifetime and sends `expired`; reconnect
      // with the fresh cookie.
      es.addEventListener('expired', () => {
        es?.close();
        scheduleReconnect();
      });

      es.onerror = () => {
        // A dropped/expired stream errors; close it (also stops the browser's tight
        // native reconnect) and reconnect with backoff. Last-Event-ID fills the gap.
        es?.close();
        es = null;
        status('closed');
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      es?.close();
      status('closed');
    };
  }, [enabled]);
}
